import { useState, useEffect, useRef } from "react";

// ── GLOBAL STYLES ─────────────────────────────────────────────────────────────
const GlobalStyle = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Outfit:wght@300;400;500;600;700&family=Space+Mono:wght@400;700&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    :root {
      --bg:        #050A10;
      --surface:   #0B1320;
      --card:      #0F1C30;
      --border:    rgba(0,210,255,0.12);
      --accent:    #00D2FF;
      --accent2:   #FF6B35;
      --accent3:   #7FFF00;
      --text:      #E8EFF8;
      --muted:     #7A8FA8;
      --heading:   'Bebas Neue', sans-serif;
      --body:      'Outfit', sans-serif;
      --mono:      'Space Mono', monospace;
    }

    html { scroll-behavior: smooth; }

    body {
      background: var(--bg);
      color: var(--text);
      font-family: var(--body);
      overflow-x: hidden;
    }

    ::selection { background: var(--accent); color: #000; }

    ::-webkit-scrollbar { width: 4px; }
    ::-webkit-scrollbar-track { background: var(--bg); }
    ::-webkit-scrollbar-thumb { background: var(--accent); border-radius: 2px; }

    /* ── NOISE OVERLAY ── */
    body::before {
      content: '';
      position: fixed; inset: 0; z-index: 0; pointer-events: none;
      background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.03'/%3E%3C/svg%3E");
      opacity: 0.4;
    }

    /* ── SCANLINE ── */
    body::after {
      content: '';
      position: fixed; inset: 0; z-index: 1; pointer-events: none;
      background: repeating-linear-gradient(
        0deg, transparent, transparent 2px, rgba(0,210,255,0.01) 2px, rgba(0,210,255,0.01) 4px
      );
    }

    .page { position: relative; z-index: 2; }

    /* ── NAV ── */
    nav {
      position: fixed; top: 0; left: 0; right: 0; z-index: 100;
      display: flex; align-items: center; justify-content: space-between;
      padding: 0 3rem;
      height: 64px;
      background: rgba(5,10,16,0.85);
      backdrop-filter: blur(20px);
      border-bottom: 1px solid var(--border);
      transition: all 0.3s;
    }

    nav.scrolled {
      height: 56px;
      background: rgba(5,10,16,0.97);
    }

    .nav-logo {
      font-family: var(--heading);
      font-size: 1.7rem;
      letter-spacing: 2px;
      color: var(--accent);
      cursor: pointer;
      display: flex; align-items: center; gap: 0.5rem;
    }

    .nav-logo span { color: var(--accent2); }

    .nav-links {
      display: flex; align-items: center; gap: 0.25rem;
      list-style: none;
    }

    .nav-links li button {
      background: none; border: none; cursor: pointer;
      font-family: var(--body); font-size: 0.85rem; font-weight: 500;
      color: var(--muted);
      padding: 0.4rem 0.8rem;
      border-radius: 4px;
      letter-spacing: 0.05em; text-transform: uppercase;
      transition: all 0.2s;
      position: relative;
    }

    .nav-links li button::after {
      content: ''; position: absolute; bottom: 0; left: 50%; right: 50%;
      height: 1px; background: var(--accent);
      transition: all 0.2s;
    }

    .nav-links li button:hover,
    .nav-links li button.active {
      color: var(--accent);
    }

    .nav-links li button:hover::after,
    .nav-links li button.active::after {
      left: 0.8rem; right: 0.8rem;
    }

    .nav-cta {
      background: var(--accent) !important;
      color: #000 !important;
      font-weight: 700 !important;
      padding: 0.5rem 1.2rem !important;
      border-radius: 3px !important;
    }

    .nav-cta::after { display: none !important; }

    .hamburger {
      display: none;
      flex-direction: column; gap: 5px;
      background: none; border: none; cursor: pointer;
      padding: 4px;
    }

    .hamburger span {
      display: block; width: 24px; height: 2px;
      background: var(--accent); border-radius: 2px;
      transition: all 0.3s;
    }

    @media (max-width: 768px) {
      nav { padding: 0 1.2rem; }
      .hamburger { display: flex; }
      .nav-links {
        display: none;
        flex-direction: column; align-items: stretch;
        position: absolute; top: 64px; left: 0; right: 0;
        background: rgba(5,10,16,0.98);
        border-bottom: 1px solid var(--border);
        padding: 1rem;
        gap: 0.1rem;
      }
      .nav-links.open { display: flex; }
    }

    /* ── SECTIONS ── */
    section { padding: 100px 0; }

    .container { max-width: 1180px; margin: 0 auto; padding: 0 2rem; }

    /* ── HERO ── */
    .hero {
      min-height: 100vh;
      display: flex; align-items: center;
      position: relative; overflow: hidden;
      padding: 120px 0 80px;
    }

    .hero-grid {
      display: grid; grid-template-columns: 1fr 1fr;
      gap: 4rem; align-items: center;
    }

    @media (max-width: 900px) {
      .hero-grid { grid-template-columns: 1fr; }
    }

    .hero-badge {
      display: inline-flex; align-items: center; gap: 0.5rem;
      background: rgba(0,210,255,0.08); border: 1px solid var(--border);
      border-radius: 2rem; padding: 0.4rem 1rem;
      font-size: 0.75rem; font-family: var(--mono);
      color: var(--accent); letter-spacing: 0.1em;
      margin-bottom: 1.5rem;
      animation: fadeUp 0.6s ease both;
    }

    .badge-dot {
      width: 7px; height: 7px; border-radius: 50%;
      background: var(--accent3);
      box-shadow: 0 0 8px var(--accent3);
      animation: pulse 2s infinite;
    }

    @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.3; } }

    .hero h1 {
      font-family: var(--heading);
      font-size: clamp(3.2rem, 6vw, 6rem);
      line-height: 0.95;
      letter-spacing: 2px;
      color: var(--text);
      animation: fadeUp 0.6s 0.1s ease both;
    }

    .hero h1 .hl { color: var(--accent); }
    .hero h1 .hl2 { color: var(--accent2); }

    .hero p {
      margin-top: 1.5rem;
      font-size: 1.05rem; color: var(--muted); line-height: 1.8;
      max-width: 520px;
      animation: fadeUp 0.6s 0.2s ease both;
    }

    .hero-btns {
      display: flex; gap: 1rem; margin-top: 2.5rem; flex-wrap: wrap;
      animation: fadeUp 0.6s 0.3s ease both;
    }

    .btn-primary {
      background: var(--accent); color: #000;
      border: none; border-radius: 3px;
      padding: 0.8rem 2rem;
      font-family: var(--body); font-weight: 700; font-size: 0.9rem;
      cursor: pointer; letter-spacing: 0.05em; text-transform: uppercase;
      transition: all 0.25s;
      position: relative; overflow: hidden;
    }

    .btn-primary::before {
      content: ''; position: absolute; inset: 0;
      background: rgba(255,255,255,0.15);
      transform: translateX(-100%);
      transition: transform 0.3s;
    }

    .btn-primary:hover::before { transform: translateX(0); }
    .btn-primary:hover { box-shadow: 0 0 30px rgba(0,210,255,0.4); transform: translateY(-2px); }

    .btn-outline {
      background: transparent;
      border: 1px solid var(--border);
      color: var(--text);
      border-radius: 3px;
      padding: 0.8rem 2rem;
      font-family: var(--body); font-weight: 500; font-size: 0.9rem;
      cursor: pointer; letter-spacing: 0.05em; text-transform: uppercase;
      transition: all 0.25s;
    }

    .btn-outline:hover {
      border-color: var(--accent); color: var(--accent);
      background: rgba(0,210,255,0.05);
    }

    /* ── HERO CANVAS VISUAL ── */
    .hero-visual {
      display: flex; align-items: center; justify-content: center;
      animation: fadeUp 0.6s 0.4s ease both;
    }

    canvas#heroCanvas {
      border: 1px solid var(--border);
      border-radius: 8px;
      box-shadow: 0 0 80px rgba(0,210,255,0.1);
    }

    /* ── STATS ── */
    .stats-bar {
      border-top: 1px solid var(--border);
      border-bottom: 1px solid var(--border);
      padding: 2rem 0;
      background: var(--surface);
    }

    .stats-inner {
      display: grid; grid-template-columns: repeat(4, 1fr);
      gap: 2rem; text-align: center;
    }

    @media (max-width: 600px) {
      .stats-inner { grid-template-columns: repeat(2, 1fr); }
    }

    .stat-num {
      font-family: var(--heading); font-size: 2.5rem;
      color: var(--accent); letter-spacing: 2px;
    }

    .stat-label {
      font-size: 0.8rem; color: var(--muted);
      text-transform: uppercase; letter-spacing: 0.1em;
      margin-top: 0.25rem;
    }

    /* ── SECTION HEADER ── */
    .section-tag {
      font-family: var(--mono); font-size: 0.72rem;
      color: var(--accent2); letter-spacing: 0.2em; text-transform: uppercase;
      display: flex; align-items: center; gap: 0.6rem;
      margin-bottom: 0.8rem;
    }

    .section-tag::before {
      content: ''; width: 30px; height: 1px; background: var(--accent2);
    }

    .section-title {
      font-family: var(--heading);
      font-size: clamp(2.2rem, 4vw, 3.6rem);
      letter-spacing: 1px; line-height: 1;
      color: var(--text);
    }

    .section-title span { color: var(--accent); }

    .section-desc {
      font-size: 1rem; color: var(--muted); line-height: 1.8;
      max-width: 580px; margin-top: 1rem;
    }

    /* ── ABOUT ── */
    .about-grid {
      display: grid; grid-template-columns: 1fr 1fr;
      gap: 5rem; align-items: center;
      margin-top: 4rem;
    }

    @media (max-width: 900px) { .about-grid { grid-template-columns: 1fr; gap: 2.5rem; } }

    .about-visual {
      position: relative;
      background: var(--card);
      border: 1px solid var(--border);
      border-radius: 8px;
      padding: 2.5rem;
      overflow: hidden;
    }

    .about-visual::before {
      content: '';
      position: absolute; top: 0; left: 0; right: 0; height: 2px;
      background: linear-gradient(90deg, transparent, var(--accent), transparent);
    }

    .terminal-line {
      font-family: var(--mono); font-size: 0.78rem;
      color: var(--accent3); line-height: 2;
    }

    .terminal-line .dim { color: var(--muted); }
    .terminal-line .acc { color: var(--accent); }
    .terminal-line .acc2 { color: var(--accent2); }

    .about-features { list-style: none; margin-top: 2rem; }

    .about-features li {
      display: flex; align-items: flex-start; gap: 1rem;
      padding: 0.9rem 0;
      border-bottom: 1px solid var(--border);
      font-size: 0.95rem; line-height: 1.6;
    }

    .about-features li:last-child { border-bottom: none; }

    .feat-icon {
      width: 34px; height: 34px; border-radius: 6px;
      background: rgba(0,210,255,0.1);
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0; font-size: 1rem;
    }

    /* ── TECH CARDS ── */
    .tech-grid {
      display: grid; grid-template-columns: repeat(3, 1fr);
      gap: 1.5rem; margin-top: 4rem;
    }

    @media (max-width: 900px) { .tech-grid { grid-template-columns: repeat(2, 1fr); } }
    @media (max-width: 560px) { .tech-grid { grid-template-columns: 1fr; } }

    .tech-card {
      background: var(--card);
      border: 1px solid var(--border);
      border-radius: 8px;
      padding: 2rem;
      position: relative; overflow: hidden;
      transition: all 0.3s;
      cursor: default;
    }

    .tech-card::before {
      content: ''; position: absolute; inset: 0;
      background: radial-gradient(circle at 50% 0%, rgba(0,210,255,0.07) 0%, transparent 60%);
      opacity: 0; transition: opacity 0.3s;
    }

    .tech-card:hover { border-color: rgba(0,210,255,0.4); transform: translateY(-4px); }
    .tech-card:hover::before { opacity: 1; }

    .tech-card-icon {
      font-size: 2.2rem; margin-bottom: 1.2rem;
      display: block;
    }

    .tech-card h3 {
      font-family: var(--heading); font-size: 1.4rem;
      letter-spacing: 1px; color: var(--text);
      margin-bottom: 0.7rem;
    }

    .tech-card p { font-size: 0.88rem; color: var(--muted); line-height: 1.7; }

    .tech-tag {
      display: inline-block; margin-top: 1.2rem;
      background: rgba(0,210,255,0.08); border: 1px solid var(--border);
      border-radius: 3px; padding: 0.2rem 0.6rem;
      font-family: var(--mono); font-size: 0.7rem;
      color: var(--accent); letter-spacing: 0.08em;
    }

    /* ── INDUSTRIES ── */
    .industries-section { background: var(--surface); }

    .ind-grid {
      display: grid; grid-template-columns: repeat(4, 1fr);
      gap: 1.2rem; margin-top: 4rem;
    }

    @media (max-width: 900px) { .ind-grid { grid-template-columns: repeat(2, 1fr); } }
    @media (max-width: 500px) { .ind-grid { grid-template-columns: 1fr; } }

    .ind-card {
      background: var(--card);
      border: 1px solid var(--border);
      border-radius: 6px;
      padding: 1.8rem 1.4rem;
      text-align: center;
      transition: all 0.3s;
    }

    .ind-card:hover {
      border-color: var(--accent2);
      transform: translateY(-4px);
      box-shadow: 0 8px 30px rgba(255,107,53,0.1);
    }

    .ind-icon { font-size: 2.5rem; margin-bottom: 1rem; display: block; }
    .ind-card h4 {
      font-family: var(--heading); font-size: 1.15rem;
      letter-spacing: 1px; margin-bottom: 0.5rem;
    }

    .ind-card p { font-size: 0.82rem; color: var(--muted); line-height: 1.6; }

    /* ── PROCESS ── */
    .process-grid {
      display: grid; grid-template-columns: repeat(4, 1fr);
      gap: 0; margin-top: 4rem; position: relative;
    }

    .process-grid::before {
      content: '';
      position: absolute; top: 3rem; left: 12.5%; right: 12.5%;
      height: 1px; background: linear-gradient(90deg, transparent, var(--accent), transparent);
    }

    @media (max-width: 768px) {
      .process-grid { grid-template-columns: 1fr 1fr; }
      .process-grid::before { display: none; }
    }

    .process-step {
      padding: 0 1.5rem;
      text-align: center;
    }

    .step-num {
      width: 56px; height: 56px;
      border-radius: 50%;
      border: 1px solid var(--accent);
      display: flex; align-items: center; justify-content: center;
      margin: 0 auto 1.5rem;
      font-family: var(--mono); font-size: 1rem;
      color: var(--accent);
      background: var(--bg);
      position: relative; z-index: 1;
    }

    .process-step h4 {
      font-family: var(--heading); font-size: 1.15rem;
      letter-spacing: 1px; margin-bottom: 0.5rem;
    }

    .process-step p { font-size: 0.83rem; color: var(--muted); line-height: 1.6; }

    /* ── CONTACT ── */
    .contact-wrap {
      display: grid; grid-template-columns: 1fr 1.4fr;
      gap: 4rem; margin-top: 4rem;
    }

    @media (max-width: 900px) { .contact-wrap { grid-template-columns: 1fr; } }

    .contact-info h3 {
      font-family: var(--heading); font-size: 1.6rem;
      letter-spacing: 1px; margin-bottom: 1.5rem;
    }

    .contact-detail {
      display: flex; align-items: flex-start; gap: 1rem;
      margin-bottom: 1.5rem;
    }

    .contact-icon {
      width: 40px; height: 40px; border-radius: 6px;
      background: rgba(0,210,255,0.1);
      border: 1px solid var(--border);
      display: flex; align-items: center; justify-content: center;
      font-size: 1.1rem; flex-shrink: 0;
    }

    .contact-detail .label { font-size: 0.75rem; color: var(--muted); text-transform: uppercase; letter-spacing: 0.1em; }
    .contact-detail .value { font-size: 0.95rem; color: var(--accent); margin-top: 0.1rem; }

    .contact-form {
      background: var(--card);
      border: 1px solid var(--border);
      border-radius: 8px;
      padding: 2.5rem;
      position: relative; overflow: hidden;
    }

    .contact-form::before {
      content: '';
      position: absolute; top: 0; left: 0; right: 0; height: 2px;
      background: linear-gradient(90deg, var(--accent), var(--accent2));
    }

    .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
    @media (max-width: 560px) { .form-row { grid-template-columns: 1fr; } }

    .form-group { margin-bottom: 1.2rem; }

    .form-group label {
      display: block; font-size: 0.78rem; color: var(--muted);
      text-transform: uppercase; letter-spacing: 0.1em;
      margin-bottom: 0.4rem; font-family: var(--mono);
    }

    .form-group input,
    .form-group textarea,
    .form-group select {
      width: 100%;
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: 4px;
      padding: 0.75rem 1rem;
      color: var(--text);
      font-family: var(--body); font-size: 0.9rem;
      transition: border-color 0.2s;
      outline: none;
      -webkit-appearance: none;
    }

    .form-group input:focus,
    .form-group textarea:focus,
    .form-group select:focus {
      border-color: var(--accent);
      box-shadow: 0 0 0 3px rgba(0,210,255,0.08);
    }

    .form-group textarea { height: 130px; resize: vertical; }

    .form-submit {
      width: 100%; padding: 1rem;
      background: var(--accent); color: #000;
      border: none; border-radius: 4px;
      font-family: var(--body); font-weight: 700; font-size: 0.95rem;
      letter-spacing: 0.08em; text-transform: uppercase;
      cursor: pointer; transition: all 0.3s;
      margin-top: 0.5rem;
    }

    .form-submit:hover {
      background: #fff;
      box-shadow: 0 0 30px rgba(0,210,255,0.4);
    }

    .form-submit:disabled {
      opacity: 0.6; cursor: not-allowed;
    }

    .form-success {
      text-align: center; padding: 3rem 1rem;
    }

    .form-success .check {
      font-size: 3rem; display: block; margin-bottom: 1rem;
    }

    .form-success h4 {
      font-family: var(--heading); font-size: 1.8rem;
      color: var(--accent3); letter-spacing: 1px;
    }

    .form-success p { color: var(--muted); margin-top: 0.5rem; font-size: 0.9rem; }

    /* ── FOOTER ── */
    footer {
      background: var(--surface);
      border-top: 1px solid var(--border);
      padding: 4rem 0 2rem;
    }

    .footer-top {
      display: grid; grid-template-columns: 2fr 1fr 1fr 1fr;
      gap: 3rem; margin-bottom: 3rem;
    }

    @media (max-width: 768px) {
      .footer-top { grid-template-columns: 1fr 1fr; gap: 2rem; }
    }

    .footer-brand .logo {
      font-family: var(--heading); font-size: 1.6rem;
      color: var(--accent); letter-spacing: 2px; margin-bottom: 1rem;
    }

    .footer-brand .logo span { color: var(--accent2); }
    .footer-brand p { font-size: 0.85rem; color: var(--muted); line-height: 1.7; }

    .footer-col h5 {
      font-family: var(--mono); font-size: 0.72rem;
      text-transform: uppercase; letter-spacing: 0.15em;
      color: var(--accent); margin-bottom: 1.2rem;
    }

    .footer-col ul { list-style: none; }
    .footer-col ul li {
      margin-bottom: 0.6rem;
    }

    .footer-col ul li button {
      background: none; border: none; cursor: pointer;
      font-family: var(--body); font-size: 0.85rem;
      color: var(--muted); transition: color 0.2s;
      padding: 0; text-align: left;
    }

    .footer-col ul li button:hover { color: var(--accent); }

    .footer-bottom {
      border-top: 1px solid var(--border); padding-top: 2rem;
      display: flex; align-items: center; justify-content: space-between;
      flex-wrap: wrap; gap: 1rem;
    }

    .footer-bottom p { font-size: 0.8rem; color: var(--muted); }

    /* ── ANIMATIONS ── */
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(20px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    .fade-in {
      opacity: 0; transform: translateY(24px);
      transition: opacity 0.6s ease, transform 0.6s ease;
    }

    .fade-in.visible { opacity: 1; transform: translateY(0); }

    /* ── HIGHLIGHT BOX ── */
    .highlight-box {
      background: rgba(0,210,255,0.05);
      border: 1px solid var(--border);
      border-radius: 6px;
      padding: 1.5rem 2rem;
      margin-top: 3rem;
    }

    .highlight-box p { font-size: 0.9rem; color: var(--muted); line-height: 1.7; }
    .highlight-box strong { color: var(--accent); }

    /* ── SHOWCASE ── */
    .showcase-tabs { display:flex; gap:1rem; margin:3rem 0 2rem; flex-wrap:wrap; }
    .showcase-tab { background:var(--card); border:1px solid var(--border); border-radius:6px; padding:1rem 1.6rem; font-family:var(--body); font-size:0.9rem; font-weight:600; color:var(--muted); cursor:pointer; transition:all 0.25s; display:flex; align-items:center; gap:0.6rem; }
    .showcase-tab:hover { border-color:var(--accent); color:var(--accent); }
    .showcase-tab.active { background:rgba(0,210,255,0.08); border-color:var(--accent); color:var(--accent); box-shadow:0 0 20px rgba(0,210,255,0.1); }
    .showcase-panel { display:none; }
    .showcase-panel.active { display:block; }
    .uc-grid { display:grid; grid-template-columns:1fr 1fr; gap:2rem; margin-bottom:2rem; }
    @media(max-width:860px){ .uc-grid { grid-template-columns:1fr; } }
    .uc-card { background:var(--card); border:1px solid var(--border); border-radius:8px; padding:1.8rem; position:relative; overflow:hidden; }
    .uc-card::before { content:''; position:absolute; top:0; left:0; right:0; height:2px; }
    .uc-card.manual::before { background:linear-gradient(90deg,#FF6B35,transparent); }
    .uc-card.ai::before { background:linear-gradient(90deg,#00D2FF,transparent); }
    .uc-card.upload::before { background:linear-gradient(90deg,#7FFF00,transparent); }
    .uc-card.report::before { background:linear-gradient(90deg,#A855F7,transparent); }
    .uc-card-label { font-family:var(--mono); font-size:0.68rem; letter-spacing:0.15em; text-transform:uppercase; margin-bottom:1rem; display:flex; align-items:center; gap:0.5rem; }
    .uc-card.manual .uc-card-label { color:#FF6B35; }
    .uc-card.ai .uc-card-label { color:#00D2FF; }
    .uc-card.upload .uc-card-label { color:#7FFF00; }
    .uc-card.report .uc-card-label { color:#A855F7; }
    .uc-card h4 { font-family:var(--heading); font-size:1.3rem; letter-spacing:1px; margin-bottom:0.8rem; }
    .uc-list { list-style:none; }
    .uc-list li { display:flex; align-items:flex-start; gap:0.6rem; font-size:0.86rem; color:var(--muted); padding:0.35rem 0; border-bottom:1px solid rgba(255,255,255,0.04); line-height:1.5; }
    .uc-list li:last-child { border-bottom:none; }
    .uc-list li .dot { flex-shrink:0; margin-top:0.35rem; width:6px; height:6px; border-radius:50%; }
    .uc-card.manual .uc-list li .dot { background:#FF6B35; }
    .uc-card.ai .uc-list li .dot { background:#00D2FF; }
    .uc-card.upload .uc-list li .dot { background:#7FFF00; }
    .uc-card.report .uc-list li .dot { background:#A855F7; }
    .proctor-wrap { background:var(--card); border:1px solid var(--border); border-radius:8px; padding:1.8rem; }
    .proctor-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
    .proctor-header h4 { font-family:var(--heading); font-size:1.2rem; letter-spacing:1px; }
    .proctor-badges { display:flex; gap:0.5rem; flex-wrap:wrap; }
    .pbadge { font-family:var(--mono); font-size:0.68rem; padding:0.25rem 0.6rem; border-radius:3px; letter-spacing:0.08em; }
    .pbadge.ok { background:rgba(127,255,0,0.1); color:#7FFF00; border:1px solid rgba(127,255,0,0.2); }
    .pbadge.warn { background:rgba(255,107,53,0.1); color:#FF6B35; border:1px solid rgba(255,107,53,0.2); }
    .pbadge.ai { background:rgba(0,210,255,0.1); color:#00D2FF; border:1px solid rgba(0,210,255,0.2); }
    .proctor-table { width:100%; border-collapse:collapse; font-size:0.82rem; margin-bottom:1.5rem; }
    .proctor-table th { font-family:var(--mono); font-size:0.68rem; text-transform:uppercase; letter-spacing:0.1em; color:var(--muted); padding:0.5rem 0.8rem; border-bottom:1px solid var(--border); text-align:left; }
    .proctor-table td { padding:0.55rem 0.8rem; border-bottom:1px solid rgba(255,255,255,0.04); color:var(--text); }
    .proctor-table tr.outlier td { color:#FF6B35; }
    .proctor-table tr.outlier td:first-child::after { content:' ⚠'; font-size:0.75rem; }
    .proctor-table td.opt { color:#7FFF00; font-weight:600; }
    .crack-demo-grid { display:grid; grid-template-columns:1fr 1fr; gap:2rem; }
    @media(max-width:720px){ .crack-demo-grid { grid-template-columns:1fr; } }
    .crack-upload-box { background:var(--card); border:2px dashed var(--border); border-radius:8px; padding:2rem; display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center; gap:1rem; cursor:pointer; transition:all 0.3s; min-height:280px; position:relative; overflow:hidden; }
    .crack-upload-box:hover { border-color:var(--accent3); }
    .crack-upload-box.has-image { border-style:solid; border-color:var(--accent3); }
    .upload-icon { font-size:2.5rem; }
    .upload-label { font-size:0.85rem; color:var(--muted); }
    .upload-sub { font-size:0.75rem; color:var(--muted); opacity:0.6; }
    .moisture-input-wrap { background:var(--card); border:1px solid var(--border); border-radius:8px; padding:1.8rem; }
    .moisture-input-wrap h4 { font-family:var(--heading); font-size:1.2rem; letter-spacing:1px; margin-bottom:1.2rem; }
    .moisture-fields { display:flex; flex-direction:column; gap:0.9rem; }
    .mfield label { display:block; font-family:var(--mono); font-size:0.7rem; text-transform:uppercase; letter-spacing:0.1em; color:var(--muted); margin-bottom:0.3rem; }
    .mfield input { width:100%; background:var(--surface); border:1px solid var(--border); border-radius:4px; padding:0.65rem 0.9rem; color:var(--text); font-family:var(--body); font-size:0.9rem; outline:none; transition:border-color 0.2s; }
    .mfield input:focus { border-color:var(--accent3); }
    .gen-report-btn { width:100%; margin-top:1.2rem; background:linear-gradient(135deg,#7FFF00,#00D2FF); color:#000; border:none; border-radius:4px; padding:0.9rem; font-family:var(--body); font-weight:700; font-size:0.9rem; letter-spacing:0.08em; text-transform:uppercase; cursor:pointer; transition:all 0.3s; }
    .gen-report-btn:hover { opacity:0.9; box-shadow:0 0 30px rgba(127,255,0,0.3); transform:translateY(-1px); }
    .gen-report-btn:disabled { opacity:0.5; cursor:not-allowed; transform:none; }
    .report-output { background:var(--surface); border:1px solid var(--border); border-radius:8px; padding:2rem; margin-top:2rem; position:relative; overflow:hidden; }
    .report-output::before { content:''; position:absolute; top:0; left:0; right:0; height:2px; background:linear-gradient(90deg,#7FFF00,#00D2FF,#A855F7); }
    .report-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
    .report-header h4 { font-family:var(--heading); font-size:1.4rem; letter-spacing:1px; color:var(--accent3); }
    .report-meta { font-family:var(--mono); font-size:0.7rem; color:var(--muted); }
    .report-section { margin-bottom:1.5rem; }
    .report-section-title { font-family:var(--mono); font-size:0.72rem; text-transform:uppercase; letter-spacing:0.15em; color:var(--accent); margin-bottom:0.6rem; padding-bottom:0.4rem; border-bottom:1px solid var(--border); }
    .report-row { display:flex; justify-content:space-between; font-size:0.85rem; padding:0.3rem 0; border-bottom:1px solid rgba(255,255,255,0.03); }
    .report-row .rkey { color:var(--muted); }
    .report-row .rval { color:var(--text); font-weight:500; }
    .report-row .rval.good { color:#7FFF00; }
    .report-row .rval.warn { color:#FF6B35; }
    .report-row .rval.info { color:#00D2FF; }
    .report-finding { font-size:0.85rem; color:var(--muted); line-height:1.7; padding:0.8rem; background:rgba(0,210,255,0.04); border-left:2px solid var(--accent); border-radius:0 4px 4px 0; margin-bottom:0.5rem; }
    .report-finding strong { color:var(--text); }
    .crack-result-badge { display:inline-flex; align-items:center; gap:0.5rem; padding:0.4rem 1rem; border-radius:3px; font-family:var(--mono); font-size:0.75rem; letter-spacing:0.1em; font-weight:700; }
    .crack-result-badge.detected { background:rgba(255,107,53,0.15); color:#FF6B35; border:1px solid rgba(255,107,53,0.3); }
    .crack-result-badge.clear { background:rgba(127,255,0,0.12); color:#7FFF00; border:1px solid rgba(127,255,0,0.3); }

  `}</style>
);

// ── HERO CANVAS ANIMATION ─────────────────────────────────────────────────────
function HeroCanvas() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;
    let frame = 0;
    let raf;

    const nodes = Array.from({ length: 28 }, (_, i) => ({
      x: Math.random() * W, y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.5, vy: (Math.random() - 0.5) * 0.5,
      r: Math.random() * 2.5 + 1,
      hue: Math.random() > 0.7 ? "#FF6B35" : "#00D2FF",
    }));

    // soil layer lines
    const layers = [
      { y: H * 0.3, label: "FILL", color: "rgba(255,107,53,0.4)" },
      { y: H * 0.5, label: "CLAY", color: "rgba(0,210,255,0.3)" },
      { y: H * 0.7, label: "ROCK", color: "rgba(127,255,0,0.2)" },
    ];

    const draw = () => {
      ctx.clearRect(0, 0, W, H);

      // Background gradient
      const bg = ctx.createLinearGradient(0, 0, 0, H);
      bg.addColorStop(0, "rgba(11,19,32,1)");
      bg.addColorStop(1, "rgba(5,10,16,1)");
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);

      // Grid
      ctx.strokeStyle = "rgba(0,210,255,0.05)";
      ctx.lineWidth = 0.5;
      for (let x = 0; x < W; x += 32) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
      }
      for (let y = 0; y < H; y += 32) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
      }

      // Soil layers
      layers.forEach(({ y, label, color }) => {
        ctx.beginPath();
        ctx.moveTo(0, y + Math.sin(frame * 0.01 + y) * 3);
        for (let x = 0; x <= W; x += 8) {
          ctx.lineTo(x, y + Math.sin(frame * 0.01 + x * 0.04) * 4);
        }
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.5;
        ctx.stroke();

        ctx.fillStyle = color;
        ctx.font = "9px 'Space Mono', monospace";
        ctx.fillText(label, 10, y - 6);
      });

      // Node connections
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 90) {
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(0,210,255,${(1 - d / 90) * 0.18})`;
            ctx.lineWidth = 0.7;
            ctx.stroke();
          }
        }
      }

      // Nodes
      nodes.forEach(n => {
        n.x += n.vx; n.y += n.vy;
        if (n.x < 0 || n.x > W) n.vx *= -1;
        if (n.y < 0 || n.y > H) n.vy *= -1;

        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = n.hue;
        ctx.shadowBlur = 8; ctx.shadowColor = n.hue;
        ctx.fill();
        ctx.shadowBlur = 0;
      });

      // Scanning line
      const scanY = ((frame * 1.2) % H);
      const scanGrad = ctx.createLinearGradient(0, scanY - 30, 0, scanY + 30);
      scanGrad.addColorStop(0, "transparent");
      scanGrad.addColorStop(0.5, "rgba(0,210,255,0.12)");
      scanGrad.addColorStop(1, "transparent");
      ctx.fillStyle = scanGrad;
      ctx.fillRect(0, scanY - 30, W, 60);

      // Corner markers
      const corners = [[10, 10], [W - 10, 10], [10, H - 10], [W - 10, H - 10]];
      corners.forEach(([cx, cy]) => {
        ctx.strokeStyle = "rgba(0,210,255,0.4)";
        ctx.lineWidth = 1;
        const s = 12, d2 = 6;
        const dx = cx === 10 ? 1 : -1;
        const dy = cy === 10 ? 1 : -1;
        ctx.beginPath(); ctx.moveTo(cx, cy + dy * d2); ctx.lineTo(cx, cy); ctx.lineTo(cx + dx * d2, cy); ctx.stroke();
      });

      // Label
      ctx.fillStyle = "rgba(0,210,255,0.5)";
      ctx.font = "8px 'Space Mono', monospace";
      ctx.fillText("GEOSHIFT·AI // SUBSURFACE ANALYSIS", 12, H - 12);

      frame++;
      raf = requestAnimationFrame(draw);
    };

    draw();
    return () => cancelAnimationFrame(raf);
  }, []);

  return <canvas ref={ref} id="heroCanvas" width={460} height={360} style={{ maxWidth: "100%", height: "auto" }} />;
}

// ── FADE-IN HOOK ──────────────────────────────────────────────────────────────
function useFadeIn() {
  useEffect(() => {
    const els = document.querySelectorAll(".fade-in");
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.1 }
    );
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  });
}

// ── PAGE COMPONENTS ───────────────────────────────────────────────────────────

function Home({ navigate }) {
  useFadeIn();
  return (
    <>
      {/* HERO */}
      <section className="hero">
        <div className="container">
          <div className="hero-grid">
            <div>
              <div className="hero-badge">
                <div className="badge-dot" />
                Next-Gen Geotechnical AI Platform
              </div>
              <h1>
                WHERE GROUND<br />
                MEETS <span className="hl">ARTIFICIAL</span><br />
                <span className="hl2">INTELLIGENCE</span>
              </h1>
              <p>
                GeoShiftAI delivers agentic AI automation for geotechnical engineering — from real-time crack monitoring to autonomous test reporting. We shift the way the earth is understood.
              </p>
              <div className="hero-btns">
                <button className="btn-primary" onClick={() => navigate("contact")}>Get in Touch</button>
                <button className="btn-outline" onClick={() => navigate("technologies")}>Explore Tech</button>
              </div>
            </div>
            <div className="hero-visual">
              <HeroCanvas />
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <div className="stats-bar">
        <div className="container">
          <div className="stats-inner">
            {[
              { n: "98%", l: "Report Accuracy" },
              { n: "10×", l: "Faster Analysis" },
              { n: "24/7", l: "Autonomous Monitoring" },
              { n: "50+", l: "Projects Delivered" },
            ].map(s => (
              <div key={s.l}>
                <div className="stat-num">{s.n}</div>
                <div className="stat-label">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* OVERVIEW CARDS */}
      <section>
        <div className="container">
          <div className="fade-in">
            <div className="section-tag">What We Do</div>
            <div className="section-title">AI-POWERED <span>GEOTECHNICAL</span> SOLUTIONS</div>
            <p className="section-desc">
              GeoShiftAI combines deep domain expertise with cutting-edge agentic AI to automate the most complex tasks in subsurface engineering.
            </p>
          </div>
          <div className="tech-grid" style={{ marginTop: "3rem" }}>
            {[
              { icon: "🔍", title: "CRACK SHIFT AI", tag: "Computer Vision", desc: "Real-time crack detection, classification, and propagation tracking using deep learning models trained on geotechnical imagery." },
              { icon: "🤖", title: "AGENTIC TESTING", tag: "Autonomous Agents", desc: "AI agents autonomously orchestrate geotechnical tests — triaxial, SPT, CPT — and generate structured reports without human intervention." },
              { icon: "📊", title: "AUTO REPORTING", tag: "LLM + Analytics", desc: "Large language models synthesize test data, observations, and standards to produce professional geotechnical reports in minutes." },
            ].map(c => (
              <div className="tech-card fade-in" key={c.title}>
                <span className="tech-card-icon">{c.icon}</span>
                <h3>{c.title}</h3>
                <p>{c.desc}</p>
                <span className="tech-tag">{c.tag}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: "2rem", textAlign: "center" }}>
            <button className="btn-outline" onClick={() => navigate("technologies")}>View All Technologies →</button>
          </div>
        </div>
      </section>
    </>
  );
}

function About({ navigate }) {
  useFadeIn();
  return (
    <section style={{ paddingTop: "120px" }}>
      <div className="container">
        <div className="fade-in">
          <div className="section-tag">Our Story</div>
          <div className="section-title">BUILT BY ENGINEERS.<br /><span>POWERED BY AI.</span></div>
          <p className="section-desc">
            GeoShiftAI was founded at the intersection of geotechnical engineering and artificial intelligence — with a singular mission: eliminate manual bottlenecks in subsurface investigation and reporting.
          </p>
        </div>

        <div className="about-grid">
          <div className="fade-in">
            <div className="about-visual">
              {[
                ["$", "geoshiftai", "--init"],
                [">", "loading", "subsurface-models v2.4"],
                [">", "connecting", "sensor-array [OK]"],
                [">", "crack-shift", "engine [ACTIVE]"],
                [">", "agentic-core", "spawning-agents..."],
                [">", "report-gen", "LLM pipeline [READY]"],
                ["✓", "system", "operational"],
              ].map(([sym, key, val], i) => (
                <div className="terminal-line" key={i}>
                  <span className="acc2">{sym} </span>
                  <span className="acc">{key} </span>
                  <span className="dim">{val}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="fade-in">
            <h3 style={{ fontFamily: "var(--heading)", fontSize: "1.5rem", letterSpacing: "1px", marginBottom: "0.8rem" }}>
              WHO WE ARE
            </h3>
            <p style={{ color: "var(--muted)", fontSize: "0.95rem", lineHeight: "1.8" }}>
              Our team combines licensed geotechnical engineers, machine learning researchers, and software architects. Together we're redefining how subsurface data is collected, analyzed, and communicated.
            </p>

            <ul className="about-features">
              {[
                { icon: "🧠", title: "AI-First Thinking", desc: "Every product is designed with autonomous intelligence at its core — not as an add-on." },
                { icon: "⚙️", title: "Deep Domain Expertise", desc: "Real geotechnical engineers who understand soil mechanics, foundations, and field conditions." },
                { icon: "🔒", title: "Standards Compliance", desc: "All automated reports align with ASTM, IS, and regional geotechnical standards." },
                { icon: "🌐", title: "Remote Capabilities", desc: "Cloud-native platform accessible from any device, anywhere in the world." },
              ].map(f => (
                <li key={f.title}>
                  <div className="feat-icon">{f.icon}</div>
                  <div>
                    <strong style={{ display: "block", marginBottom: "0.2rem", fontSize: "0.92rem" }}>{f.title}</strong>
                    <span style={{ color: "var(--muted)", fontSize: "0.86rem" }}>{f.desc}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Mission */}
        <div className="highlight-box fade-in" style={{ marginTop: "4rem" }}>
          <div className="section-tag" style={{ marginBottom: "0.6rem" }}>Our Mission</div>
          <p>
            <strong>GeoShiftAI's mission</strong> is to make geotechnical investigation faster, safer, and more intelligent through agentic AI — reducing the time from field observation to engineering decision from days to hours, without compromising on accuracy or regulatory compliance.
          </p>
        </div>

        {/* Process */}
        <div style={{ marginTop: "5rem" }}>
          <div className="fade-in" style={{ textAlign: "center" }}>
            <div className="section-tag" style={{ justifyContent: "center" }}>How It Works</div>
            <div className="section-title">THE <span>GEOSHIFTAI</span> PROCESS</div>
          </div>
          <div className="process-grid">
            {[
              { n: "01", title: "DATA CAPTURE", desc: "Field sensors, cameras, and instruments stream raw geotechnical data to our cloud platform." },
              { n: "02", title: "AI ANALYSIS", desc: "Agentic models process, classify, and interpret subsurface data in real time." },
              { n: "03", title: "CRACK SHIFT", desc: "Vision AI detects and tracks structural crack patterns with millimeter precision." },
              { n: "04", title: "AUTO REPORT", desc: "LLM agents compile findings into standards-compliant engineering reports automatically." },
            ].map(s => (
              <div className="process-step fade-in" key={s.n}>
                <div className="step-num">{s.n}</div>
                <h4>{s.title}</h4>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Technologies({ navigate }) {
  useFadeIn();
  const techs = [
    { icon: "🔍", title: "CRACK SHIFT AI", tag: "Computer Vision · Deep Learning", desc: "Our flagship crack analysis engine uses convolutional neural networks to detect, measure, and track cracks across structures and slopes in real time. Trained on thousands of geotechnical images, it classifies crack type, width, depth, and growth rate with expert-level accuracy." },
    { icon: "🤖", title: "AGENTIC TEST AUTOMATION", tag: "LLM Agents · Robotics Process Automation", desc: "Multi-agent frameworks orchestrate the full geotechnical testing workflow. Agents assign test types, monitor instrument readings, flag anomalies, and hand off findings — operating continuously without human intervention." },
    { icon: "📄", title: "INTELLIGENT REPORT GENERATION", tag: "LLM · Document AI", desc: "Large language models synthesize raw data, field observations, and applicable standards to produce compliant geotechnical reports — borehole logs, test summaries, foundation recommendations — in minutes." },
    { icon: "🌊", title: "SUBSURFACE MODELING", tag: "3D Geospatial · ML Interpolation", desc: "Machine learning-powered spatial interpolation builds 3D subsurface models from sparse borehole and test data, enabling engineers to visualize soil stratigraphy across project sites." },
    { icon: "📡", title: "IOT SENSOR INTEGRATION", tag: "Edge AI · IoT", desc: "Edge-deployed AI processes data from inclinometers, piezometers, settlement sensors, and vibration monitors in real time — triggering alerts before conditions become critical." },
    { icon: "🗺️", title: "GEOSPATIAL RISK MAPPING", tag: "GIS · Predictive Analytics", desc: "AI-driven hazard maps overlay multiple data sources to identify slope instability zones, liquefaction risk areas, and foundation suitability — supporting smarter site selection and planning." },
  ];

  return (
    <section style={{ paddingTop: "120px" }}>
      <div className="container">
        <div className="fade-in">
          <div className="section-tag">Our Platform</div>
          <div className="section-title">INTELLIGENT <span>TECHNOLOGIES</span></div>
          <p className="section-desc">
            Six interconnected AI systems that together cover the complete geotechnical investigation and reporting lifecycle.
          </p>
        </div>

        <div className="tech-grid">
          {techs.map(t => (
            <div className="tech-card fade-in" key={t.title}>
              <span className="tech-card-icon">{t.icon}</span>
              <h3>{t.title}</h3>
              <p>{t.desc}</p>
              <span className="tech-tag">{t.tag}</span>
            </div>
          ))}
        </div>

        {/* Integration banner */}
        <div className="highlight-box fade-in" style={{ marginTop: "4rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
            <span style={{ fontSize: "2rem" }}>🔗</span>
            <div>
              <strong style={{ color: "var(--accent)", display: "block", marginBottom: "0.3rem", fontFamily: "var(--heading)", fontSize: "1.2rem", letterSpacing: "1px" }}>FULLY INTEGRATED STACK</strong>
              <p style={{ color: "var(--muted)", fontSize: "0.9rem" }}>
                All GeoShiftAI technologies share a unified data layer — meaning insights from crack monitoring automatically inform report generation, which feeds back into risk models. <strong style={{ color: "var(--accent)" }}>One platform. Complete intelligence.</strong>
              </p>
            </div>
          </div>
        </div>

        <div style={{ marginTop: "3rem", textAlign: "center" }}>
          <button className="btn-primary" onClick={() => navigate("contact")}>Request a Demo</button>
        </div>
      </div>
    </section>
  );
}

function Industries({ navigate }) {
  useFadeIn();
  const inds = [
    { icon: "🏗️", title: "INFRASTRUCTURE", desc: "Highways, bridges, tunnels, and retaining walls — AI-assisted foundation assessment and structural monitoring." },
    { icon: "⛏️", title: "MINING & QUARRYING", desc: "Slope stability monitoring, tailings dam analysis, and blast vibration assessment using agentic AI." },
    { icon: "🌆", title: "REAL ESTATE & CONSTRUCTION", desc: "Site investigation automation and AI-generated geotechnical reports for residential and commercial developments." },
    { icon: "💧", title: "DAMS & WATER", desc: "Continuous seepage monitoring, settlement tracking, and automated safety reports for dams and levees." },
    { icon: "⚡", title: "ENERGY & UTILITIES", desc: "Foundation integrity checks for wind turbines, transmission towers, and pipeline route surveys." },
    { icon: "🌿", title: "ENVIRONMENTAL", desc: "Contamination mapping, landfill monitoring, and slope stability for environmental engineering projects." },
    { icon: "🛤️", title: "RAILWAYS", desc: "Embankment stability, track settlement monitoring, and AI-assisted geohazard mapping for rail corridors." },
    { icon: "🏛️", title: "GOVERNMENT & DEFENSE", desc: "Strategic subsurface intelligence, emergency slope stabilization, and regulatory-grade automated reporting." },
  ];

  return (
    <section className="industries-section" style={{ paddingTop: "120px" }}>
      <div className="container">
        <div className="fade-in">
          <div className="section-tag">Sectors We Serve</div>
          <div className="section-title">INDUSTRIES WE <span>TRANSFORM</span></div>
          <p className="section-desc">
            GeoShiftAI's technology is deployed across the full spectrum of industries that depend on understanding the ground beneath.
          </p>
        </div>

        <div className="ind-grid">
          {inds.map(i => (
            <div className="ind-card fade-in" key={i.title}>
              <span className="ind-icon">{i.icon}</span>
              <h4>{i.title}</h4>
              <p>{i.desc}</p>
            </div>
          ))}
        </div>

        <div style={{ marginTop: "4rem", textAlign: "center" }} className="fade-in">
          <p style={{ color: "var(--muted)", fontSize: "0.95rem", marginBottom: "1.5rem" }}>
            Working in a sector not listed above? We work with any industry where subsurface conditions matter.
          </p>
          <button className="btn-primary" onClick={() => navigate("contact")}>Discuss Your Project</button>
        </div>
      </div>
    </section>
  );
}

// ── EMAILJS CONFIG ────────────────────────────────────────────────────────────
const EMAILJS_SERVICE_ID  = "service_8vxu1fc";
const EMAILJS_TEMPLATE_ID = "template_vx8hnoc";
const EMAILJS_PUBLIC_KEY  = "hfPFs9x76Ht_ehaTC";

async function sendEmailJS(templateParams) {
  const url = "https://api.emailjs.com/api/v1.0/email/send";
  const payload = {
    service_id:  EMAILJS_SERVICE_ID,
    template_id: EMAILJS_TEMPLATE_ID,
    user_id:     EMAILJS_PUBLIC_KEY,
    template_params: templateParams,
  };
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`EmailJS error: ${res.status}`);
  return res;
}

function Contact() {
  useFadeIn();
  const [form, setForm] = useState({ name: "", email: "", company: "", service: "", message: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const handle = e => { setError(""); setForm(f => ({ ...f, [e.target.name]: e.target.value })); };

  const submit = async e => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      await sendEmailJS({
        name:    form.name,
        email:   form.email,
        company: form.company || "Not provided",
        service: form.service || "General Inquiry",
        message: form.message,
        to_email: "info@geoshiftai.com",
        reply_to: form.email,
      });
      setSent(true);
    } catch (err) {
      setError("Failed to send. Please email us directly at info@geoshiftai.com");
    } finally {
      setSending(false);
    }
  };

  return (
    <section style={{ paddingTop: "120px" }}>
      <div className="container">
        <div className="fade-in">
          <div className="section-tag">Get In Touch</div>
          <div className="section-title">CONTACT <span>GEOSHIFTAI</span></div>
          <p className="section-desc">
            Ready to bring AI to your geotechnical workflow? Our team is here to discuss your project, schedule a demo, or answer any technical questions.
          </p>
        </div>

        <div className="contact-wrap">
          {/* Info */}
          <div className="fade-in">
            <h3>LET'S BUILD SOMETHING INTELLIGENT</h3>
            {[
              { icon: "✉️", label: "Email Us", value: "info@geoshiftai.com" },
              { icon: "🌐", label: "Platform", value: "www.geoshiftai.com" },
              { icon: "🤝", label: "Services", value: "AI Consulting · Crack Analysis · Auto Reporting" },
              { icon: "⏱️", label: "Response Time", value: "Within 24 business hours" },
            ].map(d => (
              <div className="contact-detail" key={d.label}>
                <div className="contact-icon">{d.icon}</div>
                <div>
                  <div className="label">{d.label}</div>
                  <div className="value">{d.value}</div>
                </div>
              </div>
            ))}

            <div className="highlight-box" style={{ marginTop: "2rem" }}>
              <p>
                <strong>First call is free.</strong> We offer a complimentary 30-minute discovery session to understand your project needs before any commitment.
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="fade-in">
            <div className="contact-form">
              {sent ? (
                <div className="form-success">
                  <span className="check">✅</span>
                  <h4>MESSAGE SENT!</h4>
                  <p>Your message has been delivered to info@geoshiftai.com.<br />We'll be in touch within 24 hours.</p>
                  <button className="btn-outline" style={{ marginTop: "2rem" }} onClick={() => { setSent(false); setForm({ name:"", email:"", company:"", service:"", message:"" }); }}>Send Another</button>
                </div>
              ) : (
                <form onSubmit={submit}>
                  {error && (
                    <div style={{ background:"rgba(255,80,80,0.1)", border:"1px solid rgba(255,80,80,0.3)", borderRadius:"4px", padding:"0.8rem 1rem", marginBottom:"1rem", fontSize:"0.85rem", color:"#ff6b6b" }}>
                      ⚠️ {error}
                    </div>
                  )}
                  <div className="form-row">
                    <div className="form-group">
                      <label>Full Name *</label>
                      <input name="name" value={form.name} onChange={handle} required placeholder="Your name" />
                    </div>
                    <div className="form-group">
                      <label>Email Address *</label>
                      <input type="email" name="email" value={form.email} onChange={handle} required placeholder="you@company.com" />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Company / Organization</label>
                    <input name="company" value={form.company} onChange={handle} placeholder="Your organization name" />
                  </div>
                  <div className="form-group">
                    <label>Service of Interest</label>
                    <select name="service" value={form.service} onChange={handle}>
                      <option value="">Select a service…</option>
                      <option>Crack Shift AI — Crack Detection & Monitoring</option>
                      <option>Agentic Test Automation</option>
                      <option>Intelligent Report Generation</option>
                      <option>Subsurface 3D Modeling</option>
                      <option>IoT Sensor Integration</option>
                      <option>Geospatial Risk Mapping</option>
                      <option>Custom AI Solution</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Message *</label>
                    <textarea name="message" value={form.message} onChange={handle} required placeholder="Describe your project or question…" />
                  </div>
                  <button className="form-submit" type="submit" disabled={sending}>
                    {sending ? "⏳ SENDING…" : "SEND MESSAGE →"}
                  </button>
                  <p style={{ fontSize: "0.75rem", color: "var(--muted)", marginTop: "0.8rem", textAlign: "center" }}>
                    🔒 Your message is sent directly & securely to info@geoshiftai.com
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


// ── PROCTOR CURVE SVG ──────────────────────────────────────────────────────────
function ProctorCurve() {
  const pts = [
    {mc:8,  dd:1.72}, {mc:10, dd:1.81}, {mc:12, dd:1.89},
    {mc:14, dd:1.95}, {mc:16, dd:1.98}, {mc:18, dd:1.94},
    {mc:20, dd:1.87}, {mc:22, dd:1.78},
  ];
  const W=460, H=240, pl=52, pr=20, pt=20, pb=40;
  const cw=W-pl-pr, ch=H-pt-pb;
  const xMin=6, xMax=24, yMin=1.65, yMax=2.05;
  const px = mc => pl + ((mc-xMin)/(xMax-xMin))*cw;
  const py = dd => pt + ch - ((dd-yMin)/(yMax-yMin))*ch;
  const pathD = pts.map((p,i)=>`${i===0?'M':'L'}${px(p.mc).toFixed(1)},${py(p.dd).toFixed(1)}`).join(' ');
  const optPt = pts[4];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',background:'var(--surface)',borderRadius:'6px',border:'1px solid var(--border)'}}>
      {[1.70,1.75,1.80,1.85,1.90,1.95,2.00].map(v=>(
        <g key={v}>
          <line x1={pl} y1={py(v)} x2={W-pr} y2={py(v)} stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
          <text x={pl-6} y={py(v)+4} fontSize="9" fill="#7A8FA8" textAnchor="end">{v.toFixed(2)}</text>
        </g>
      ))}
      {[8,10,12,14,16,18,20,22].map(v=>(
        <g key={v}>
          <line x1={px(v)} y1={pt} x2={px(v)} y2={H-pb} stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
          <text x={px(v)} y={H-pb+14} fontSize="9" fill="#7A8FA8" textAnchor="middle">{v}%</text>
        </g>
      ))}
      <path d={pathD} fill="none" stroke="#00D2FF" strokeWidth="2.5" strokeLinejoin="round"/>
      {pts.map((p,i)=>(
        <circle key={i} cx={px(p.mc)} cy={py(p.dd)} r="4"
          fill={p.mc===20?"#FF6B35":"#00D2FF"}
          stroke={p.mc===20?"rgba(255,107,53,0.3)":"rgba(0,210,255,0.3)"} strokeWidth="6"/>
      ))}
      <line x1={px(optPt.mc)} y1={pt} x2={px(optPt.mc)} y2={H-pb} stroke="rgba(127,255,0,0.4)" strokeWidth="1.5" strokeDasharray="4,3"/>
      <text x={px(optPt.mc)+5} y={pt+14} fontSize="9" fill="#7FFF00">OMC = {optPt.mc}%</text>
      <text x={px(pts[5].mc)+5} y={py(pts[5].dd)-8} fontSize="9" fill="#FF6B35">⚠ Outlier</text>
      <text x={pl} y={H-4} fontSize="9" fill="#7A8FA8">Moisture Content (%)</text>
      <text x={10} y={pt+ch/2} fontSize="9" fill="#7A8FA8" transform={`rotate(-90,10,${pt+ch/2})`}>Dry Density (g/cc)</text>
      <text x={W/2} y={pt+14} fontSize="10" fill="#00D2FF" textAnchor="middle">PROCTOR COMPACTION CURVE — AI GENERATED</text>
    </svg>
  );
}

// ── CRACK CANVAS DEMO ─────────────────────────────────────────────────────────
function CrackCanvas({ imageData }) {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;
    ctx.fillStyle = "#0B1320";
    ctx.fillRect(0, 0, W, H);
    if (imageData) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, W, H);
        // overlay crack detection boxes
        ctx.strokeStyle = "#FF6B35"; ctx.lineWidth = 2;
        ctx.strokeRect(W*0.2, H*0.15, W*0.25, H*0.2);
        ctx.strokeRect(W*0.55, H*0.45, W*0.3, H*0.25);
        ctx.fillStyle = "rgba(255,107,53,0.15)";
        ctx.fillRect(W*0.2, H*0.15, W*0.25, H*0.2);
        ctx.fillRect(W*0.55, H*0.45, W*0.3, H*0.25);
        ctx.fillStyle = "#FF6B35"; ctx.font = "bold 10px 'Space Mono'";
        ctx.fillText("CRACK #1  0.8mm", W*0.2+4, H*0.15-5);
        ctx.fillText("CRACK #2  1.2mm", W*0.55+4, H*0.45-5);
        ctx.strokeStyle = "rgba(0,210,255,0.5)"; ctx.lineWidth = 1;
        ctx.strokeRect(2, 2, W-4, H-4);
        ctx.fillStyle = "rgba(0,210,255,0.7)"; ctx.font = "9px 'Space Mono'";
        ctx.fillText("CRACK·SHIFT·AI — ANALYSIS COMPLETE", 8, H-8);
      };
      img.src = imageData;
    } else {
      ctx.fillStyle = "rgba(0,210,255,0.06)";
      for (let x=0;x<W;x+=24) { ctx.fillRect(x,0,1,H); }
      for (let y=0;y<H;y+=24) { ctx.fillRect(0,y,W,1); }
      ctx.fillStyle = "rgba(0,210,255,0.2)";
      ctx.font = "11px 'Space Mono'"; ctx.textAlign="center";
      ctx.fillText("Upload soil image to", W/2, H/2-10);
      ctx.fillText("activate Crack Shift AI", W/2, H/2+10);
      ctx.textAlign="left";
    }
  }, [imageData]);
  return <canvas ref={ref} width={340} height={260} style={{width:"100%",borderRadius:"6px"}}/>;
}

// ── SHOWCASE PAGE ─────────────────────────────────────────────────────────────
function Showcase({ navigate }) {
  useFadeIn();
  const [activeTab, setActiveTab] = useState(0);

  // Use case 1 — Proctor
  const proctorData = [
    {mc:8,  dd:1.72, flag:false}, {mc:10, dd:1.81, flag:false},
    {mc:12, dd:1.89, flag:false}, {mc:14, dd:1.95, flag:false},
    {mc:16, dd:1.98, flag:false}, {mc:18, dd:1.94, flag:false},
    {mc:20, dd:1.87, flag:true},  {mc:22, dd:1.78, flag:false},
  ];

  // Use case 2 — Crack + Report
  const [imageData, setImageData] = useState(null);
  const [moisture, setMoisture] = useState({ wn:"18.5", ll:"42", pl:"21", gs:"2.67", depth:"3.0" });
  const [report, setReport] = useState(null);
  const [generating, setGenerating] = useState(false);
  const fileRef = useRef(null);

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setImageData(ev.target.result);
    reader.readAsDataURL(file);
  };

  const generateReport = async () => {
    setGenerating(true);
    await new Promise(r => setTimeout(r, 1800));
    const wn = parseFloat(moisture.wn);
    const ll = parseFloat(moisture.ll);
    const pl = parseFloat(moisture.pl);
    const pi = ll - pl;
    const gs = parseFloat(moisture.gs);
    const e = (gs * 1.0 / 1.65) - 1;
    const sr = (wn/100 * gs / e * 100).toFixed(1);
    const classification = pi > 17 ? "CH — High Plasticity Clay" : pi > 7 ? "CL — Low Plasticity Clay" : "ML — Silt";
    const crackSeverity = imageData ? (wn > 20 ? "Moderate — Desiccation cracking likely" : "Minor — Surface shrinkage") : "No image provided";
    setReport({ wn, ll, pl, pi: pi.toFixed(1), gs, sr, classification, crackSeverity, depth: moisture.depth, date: new Date().toLocaleDateString("en-GB") });
    setGenerating(false);
  };

  return (
    <section style={{paddingTop:"120px"}}>
      <div className="container">
        <div className="fade-in">
          <div className="section-tag">Live Demos</div>
          <div className="section-title">USE CASE <span>SHOWCASE</span></div>
          <p className="section-desc">Interactive demonstrations of GeoShiftAI's two flagship automation systems. Try them live below.</p>
        </div>

        <div className="showcase-tabs">
          {[
            { icon:"📊", label:"Use Case 1 — Proctor Test Automation" },
            { icon:"🔍", label:"Use Case 2 — Crack Shift + Report Generator" },
          ].map((t,i) => (
            <button key={i} className={`showcase-tab${activeTab===i?" active":""}`} onClick={()=>setActiveTab(i)}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* ── USE CASE 1 ── */}
        <div className={`showcase-panel${activeTab===0?" active":""}`}>
          <div className="uc-grid">
            <div className="uc-card manual fade-in">
              <div className="uc-card-label">⚠ Before — Manual Process</div>
              <h4>MANUAL EXCEL WORKFLOW</h4>
              <ul className="uc-list">
                {["Manual data entry into Excel spreadsheet","Re-checking formulas cell by cell","Plotting Proctor curve manually","Identifying outliers by visual inspection","Writing compaction report from scratch","Hours of engineer time per test"].map(t=>(
                  <li key={t}><span className="dot"/>{t}</li>
                ))}
              </ul>
            </div>
            <div className="uc-card ai fade-in">
              <div className="uc-card-label">✦ After — AI Agent Solution</div>
              <h4>AUTOMATION OPPORTUNITY</h4>
              <ul className="uc-list">
                {["Agent auto-calculates dry density & OMC","Detects abnormal moisture content values","Flags outliers and suggests retest automatically","Auto-generates Proctor curve with annotations","Produces compaction report in minutes","Zero manual formula work required"].map(t=>(
                  <li key={t}><span className="dot"/>{t}</li>
                ))}
              </ul>
            </div>
          </div>

          <div className="proctor-wrap fade-in">
            <div className="proctor-header">
              <h4>PROCTOR COMPACTION DATA — AI ANALYSIS</h4>
              <div className="proctor-badges">
                <span className="pbadge ok">✓ OMC Detected: 16%</span>
                <span className="pbadge warn">⚠ Outlier at 20%</span>
                <span className="pbadge ai">AI Agent Active</span>
              </div>
            </div>
            <table className="proctor-table">
              <thead>
                <tr>
                  <th>Moisture Content (%)</th>
                  <th>Dry Density (g/cc)</th>
                  <th>AI Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {proctorData.map((row,i) => (
                  <tr key={i} className={row.flag?"outlier":""}>
                    <td>{row.mc}%</td>
                    <td className={row.mc===16?"opt":""}>{row.dd}</td>
                    <td>{row.mc===16 ? <span style={{color:"#7FFF00",fontSize:"0.78rem"}}>★ OPTIMUM</span> : row.flag ? <span style={{fontSize:"0.78rem"}}>Abnormal Value</span> : <span style={{color:"var(--muted)",fontSize:"0.78rem"}}>Normal</span>}</td>
                    <td style={{fontSize:"0.78rem",color:"var(--muted)"}}>{row.flag ? "⟳ Suggest Retest" : row.mc===16 ? "Record as OMC" : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <ProctorCurve />
            <div style={{marginTop:"1rem",padding:"1rem",background:"rgba(0,210,255,0.05)",borderLeft:"2px solid var(--accent)",borderRadius:"0 4px 4px 0"}}>
              <p style={{fontSize:"0.84rem",color:"var(--muted)",lineHeight:"1.7"}}>
                <strong style={{color:"var(--accent)"}}>AI Agent Summary:</strong> Optimum Moisture Content identified at <strong style={{color:"#7FFF00"}}>16%</strong> with Maximum Dry Density of <strong style={{color:"#7FFF00"}}>1.98 g/cc</strong>. Data point at 20% moisture flagged as outlier — dry density drop exceeds expected curve trend. <strong style={{color:"#FF6B35"}}>Retest recommended</strong> for that specimen. Proctor curve plotted and compaction report auto-generated.
              </p>
            </div>
          </div>
        </div>

        {/* ── USE CASE 2 ── */}
        <div className={`showcase-panel${activeTab===1?" active":""}`}>
          <div className="uc-grid">
            <div className="uc-card upload fade-in">
              <div className="uc-card-label">📷 Step 1 — Upload Soil Image</div>
              <h4>CRACK SHIFT AI</h4>
              <ul className="uc-list">
                {["Upload photo of soil/structure surface","AI detects crack locations automatically","Measures crack width in millimeters","Classifies crack type & severity","Highlights affected zones on image","Feeds findings directly into report"].map(t=>(
                  <li key={t}><span className="dot"/>{t}</li>
                ))}
              </ul>
            </div>
            <div className="uc-card report fade-in">
              <div className="uc-card-label">📄 Step 2 — Provide Soil Parameters</div>
              <h4>AUTO REPORT GENERATION</h4>
              <ul className="uc-list">
                {["Enter moisture content & Atterberg limits","AI calculates PI, classification, saturation","Combines crack data + lab data","Generates full geotechnical report","Standards-compliant output in minutes","One-click download ready"].map(t=>(
                  <li key={t}><span className="dot"/>{t}</li>
                ))}
              </ul>
            </div>
          </div>

          <div className="crack-demo-grid fade-in">
            <div>
              <div
                className={`crack-upload-box${imageData?" has-image":""}`}
                onClick={()=>fileRef.current?.click()}
              >
                {!imageData && <>
                  <span className="upload-icon">🖼️</span>
                  <span className="upload-label">Click to upload soil image</span>
                  <span className="upload-sub">JPG, PNG supported — Crack Shift AI activates on upload</span>
                </>}
                <CrackCanvas imageData={imageData}/>
                <input ref={fileRef} type="file" accept="image/*" style={{display:"none"}} onChange={handleImage}/>
              </div>
              {imageData && (
                <div style={{marginTop:"0.8rem",display:"flex",gap:"0.6rem",flexWrap:"wrap"}}>
                  <span className="crack-result-badge detected">⚠ 2 Cracks Detected</span>
                  <span className="crack-result-badge detected">Max Width: 1.2mm</span>
                  <button style={{background:"none",border:"1px solid var(--border)",borderRadius:"3px",color:"var(--muted)",fontSize:"0.75rem",padding:"0.3rem 0.7rem",cursor:"pointer"}} onClick={()=>setImageData(null)}>Clear ✕</button>
                </div>
              )}
            </div>

            <div className="moisture-input-wrap">
              <h4>SOIL PARAMETERS</h4>
              <div className="moisture-fields">
                {[
                  {key:"wn",  label:"Natural Moisture Content (%)", placeholder:"e.g. 18.5"},
                  {key:"ll",  label:"Liquid Limit (%)",              placeholder:"e.g. 42"},
                  {key:"pl",  label:"Plastic Limit (%)",             placeholder:"e.g. 21"},
                  {key:"gs",  label:"Specific Gravity",              placeholder:"e.g. 2.67"},
                  {key:"depth",label:"Sample Depth (m)",             placeholder:"e.g. 3.0"},
                ].map(f => (
                  <div className="mfield" key={f.key}>
                    <label>{f.label}</label>
                    <input value={moisture[f.key]} onChange={e=>setMoisture(m=>({...m,[f.key]:e.target.value}))} placeholder={f.placeholder}/>
                  </div>
                ))}
              </div>
              <button className="gen-report-btn" onClick={generateReport} disabled={generating}>
                {generating ? "⏳ GENERATING REPORT…" : "⚡ GENERATE REPORT"}
              </button>
            </div>
          </div>

          {report && (
            <div className="report-output fade-in">
              <div className="report-header">
                <h4>✦ GEOTECHNICAL REPORT — AUTO GENERATED</h4>
                <div className="report-meta">Generated: {report.date} · GeoShiftAI v2.4</div>
              </div>

              <div className="report-section">
                <div className="report-section-title">01 · Sample Information</div>
                <div className="report-row"><span className="rkey">Sample Depth</span><span className="rval info">{report.depth} m BGL</span></div>
                <div className="report-row"><span className="rkey">Test Date</span><span className="rval">{report.date}</span></div>
                <div className="report-row"><span className="rkey">Analysis Engine</span><span className="rval info">GeoShiftAI Agentic Core v2.4</span></div>
              </div>

              <div className="report-section">
                <div className="report-section-title">02 · Laboratory Results</div>
                <div className="report-row"><span className="rkey">Natural Moisture Content (wn)</span><span className="rval">{report.wn}%</span></div>
                <div className="report-row"><span className="rkey">Liquid Limit (LL)</span><span className="rval">{report.ll}%</span></div>
                <div className="report-row"><span className="rkey">Plastic Limit (PL)</span><span className="rval">{report.pl}%</span></div>
                <div className="report-row"><span className="rkey">Plasticity Index (PI)</span><span className={`rval ${parseFloat(report.pi)>17?"warn":"good"}`}>{report.pi}%</span></div>
                <div className="report-row"><span className="rkey">Specific Gravity (Gs)</span><span className="rval">{report.gs}</span></div>
                <div className="report-row"><span className="rkey">Degree of Saturation</span><span className="rval">{report.sr}%</span></div>
              </div>

              <div className="report-section">
                <div className="report-section-title">03 · AI Classification & Findings</div>
                <div className="report-row"><span className="rkey">Soil Classification (USCS)</span><span className="rval info">{report.classification}</span></div>
                <div className="report-row"><span className="rkey">Crack Shift Analysis</span><span className={`rval ${imageData?"warn":"good"}`}>{report.crackSeverity}</span></div>
              </div>

              <div className="report-section">
                <div className="report-section-title">04 · Engineering Recommendations</div>
                <div className="report-finding">
                  <strong>Moisture Condition:</strong> Natural moisture content of {report.wn}% is {parseFloat(report.wn) > parseFloat(report.ll) ? "above liquid limit — soil in near-liquid state, low bearing capacity expected" : parseFloat(report.wn) > parseFloat(report.pl) ? "within plastic range — moderate workability" : "below plastic limit — stiff to hard consistency"}.
                </div>
                <div className="report-finding">
                  <strong>Foundation Suitability:</strong> {parseFloat(report.pi) > 17 ? "High plasticity clay detected. Swelling/shrinkage potential is significant. Deep foundations or soil improvement recommended." : "Low to medium plasticity soil. Shallow foundations may be suitable subject to bearing capacity verification."}
                </div>
                {imageData && (
                  <div className="report-finding">
                    <strong>Crack Assessment:</strong> Surface cracking detected via Crack Shift AI. Maximum crack width 1.2mm. {parseFloat(report.wn) > 20 ? "Elevated moisture content may indicate active desiccation. Monitor crack propagation rate." : "Cracking consistent with surface drying. No immediate structural concern, but periodic monitoring advised."}
                  </div>
                )}
              </div>

              <div style={{textAlign:"center",marginTop:"1.5rem"}}>
                <span style={{fontSize:"0.75rem",color:"var(--muted)",fontFamily:"var(--mono)"}}>
                  ★ This report was generated autonomously by GeoShiftAI · info@geoshiftai.com
                </span>
              </div>
            </div>
          )}
        </div>

        <div style={{marginTop:"4rem",textAlign:"center"}} className="fade-in">
          <button className="btn-primary" onClick={()=>navigate("contact")}>Get This For Your Project →</button>
        </div>
      </div>
    </section>
  );
}

// ── APP ───────────────────────────────────────────────────────────────────────
export default function App() {
  const [page, setPage] = useState("home");
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const navigate = (p) => { setPage(p); setMenuOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); };

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const navItems = [
    { id: "home", label: "Home" },
    { id: "about", label: "About Us" },
    { id: "technologies", label: "Technologies" },
    { id: "industries", label: "Industries" },
    { id: "showcase", label: "Showcase" },
    { id: "contact", label: "Contact Us", cta: true },
  ];

  return (
    <>
      <GlobalStyle />
      <div className="page">
        <nav className={scrolled ? "scrolled" : ""}>
          <div className="nav-logo" onClick={() => navigate("home")}>
            GEO<span>SHIFT</span>AI
          </div>

          <ul className={`nav-links${menuOpen ? " open" : ""}`}>
            {navItems.map(item => (
              <li key={item.id}>
                <button
                  className={`${page === item.id ? "active" : ""}${item.cta ? " nav-cta" : ""}`}
                  onClick={() => navigate(item.id)}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>

          <button className="hamburger" onClick={() => setMenuOpen(v => !v)} aria-label="Menu">
            <span /><span /><span />
          </button>
        </nav>

        {page === "home"         && <Home navigate={navigate} />}
        {page === "about"        && <About navigate={navigate} />}
        {page === "technologies" && <Technologies navigate={navigate} />}
        {page === "industries"   && <Industries navigate={navigate} />}
        {page === "contact"      && <Contact />}
        {page === "showcase"     && <Showcase navigate={navigate} />}

        <footer>
          <div className="container">
            <div className="footer-top">
              <div className="footer-brand">
                <div className="logo">GEO<span>SHIFT</span>AI</div>
                <p>Agentic AI for geotechnical engineering. Crack detection, autonomous testing, and intelligent report generation — all in one platform.</p>
              </div>
              <div className="footer-col">
                <h5>Platform</h5>
                <ul>
                  {["Crack Shift AI", "Agentic Testing", "Auto Reports", "Sensor Integration"].map(l => (
                    <li key={l}><button onClick={() => navigate("technologies")}>{l}</button></li>
                  ))}
                </ul>
              </div>
              <div className="footer-col">
                <h5>Industries</h5>
                <ul>
                  {["Infrastructure", "Mining", "Real Estate", "Energy"].map(l => (
                    <li key={l}><button onClick={() => navigate("industries")}>{l}</button></li>
                  ))}
                </ul>
              </div>
              <div className="footer-col">
                <h5>Company</h5>
                <ul>
                  {[["About Us", "about"], ["Technologies", "technologies"], ["Showcase", "showcase"], ["Contact", "contact"]].map(([l, p]) => (
                    <li key={l}><button onClick={() => navigate(p)}>{l}</button></li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="footer-bottom">
              <p>© {new Date().getFullYear()} GeoShiftAI. All rights reserved.</p>
              <p style={{ color: "var(--accent)", fontFamily: "var(--mono)", fontSize: "0.75rem" }}>
                info@geoshiftai.com
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
